import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge } from '../components/common/Badge';
import { EmptyState } from '../components/common/EmptyState';
import { SkeletonDetail } from '../components/common/SkeletonDetail';
import { VersionHistoryTab } from '../components/system-info/VersionHistoryTab';
import { apiClient } from '../services/api';
import type { ComponentInfo, SystemInfoResponse } from '../types';
import { formatRelativeTime, formatTimestamp } from '../utils/dateFormat';

declare const __BUILD_COMMIT__: string;
declare const __BUILD_TIMESTAMP__: string;

type ActiveTab = 'health-status' | 'version-history';

const FRONTEND_COMPONENT_NAME = 'Frontend (Admin Portal)';

function normalizeStatus(status?: string): string {
  const normalized = status?.trim().toLowerCase();
  if (normalized === 'healthy') return 'healthy';
  if (normalized === 'degraded') return 'degraded';
  if (normalized === 'unhealthy') return 'unhealthy';
  if (normalized === 'error') return 'error';
  if (normalized === 'timeout') return 'timeout';
  if (normalized === 'unreachable') return 'unreachable';
  if (normalized === 'not_configured') return 'not_configured';
  if (normalized === 'self_reported') return 'self_reported';
  return 'unknown';
}

function getStatusVariant(status?: string): 'success' | 'warning' | 'error' | 'neutral' {
  const normalized = normalizeStatus(status);
  if (normalized === 'healthy' || normalized === 'self_reported') return 'success';
  if (normalized === 'degraded') return 'warning';
  if (normalized === 'unhealthy' || normalized === 'error' || normalized === 'timeout' || normalized === 'unreachable') return 'error';
  return 'neutral';
}

function getStatusLabel(status?: string): string {
  const normalized = normalizeStatus(status);
  if (normalized === 'healthy') return 'Healthy';
  if (normalized === 'degraded') return 'Degraded';
  if (normalized === 'unhealthy') return 'Unhealthy';
  if (normalized === 'error') return 'Error';
  if (normalized === 'timeout') return 'Timeout';
  if (normalized === 'unreachable') return 'Unreachable';
  if (normalized === 'not_configured') return 'Not configured';
  if (normalized === 'self_reported') return 'Self reported';
  return 'Unknown';
}

function truncateCommitSha(sha?: string): string {
  if (!sha) return '—';
  return sha.slice(0, 7);
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{value}</dd>
    </div>
  );
}

function getOverallStatus(components: ComponentInfo[]): string {
  if (components.length === 0) return 'unknown';
  const hasUnhealthy = components.some((component) =>
    ['unhealthy', 'error', 'timeout', 'unreachable'].includes(normalizeStatus(component.status)));
  if (hasUnhealthy) return 'unhealthy';
  const hasDegraded = components.some((component) =>
    ['degraded', 'not_configured', 'unknown'].includes(normalizeStatus(component.status)));
  if (hasDegraded) return 'degraded';
  return 'healthy';
}

function applyFrontendBuildOverride(systemInfo: SystemInfoResponse): SystemInfoResponse {
  const components = Array.isArray(systemInfo.components)
    ? systemInfo.components.map((component) => ({ ...component }))
    : [];

  const frontendIndex = components.findIndex((component) => component.name.toLowerCase().includes('frontend'));
  const frontendBase = frontendIndex >= 0 ? components[frontendIndex] : { name: FRONTEND_COMPONENT_NAME, status: 'healthy' };
  const frontendComponent: ComponentInfo = {
    ...frontendBase,
    name: FRONTEND_COMPONENT_NAME,
    version: import.meta.env.VITE_APP_VERSION || '0.0.0',
    commitSha: __BUILD_COMMIT__ === 'local' ? undefined : __BUILD_COMMIT__,
    buildTimestamp: __BUILD_TIMESTAMP__,
    status: 'healthy',
    statusMessage: undefined,
  };

  if (frontendIndex >= 0) {
    components[frontendIndex] = frontendComponent;
  } else {
    components.push(frontendComponent);
  }

  return {
    ...systemInfo,
    components,
  };
}

function HealthStatusTab({
  systemInfo,
}: {
  systemInfo: SystemInfoResponse;
}) {
  const components = systemInfo.components || [];
  const overallStatus = getOverallStatus(components);
  const backendComponent = components.find((component) => component.name === 'Backend API');
  const frontendComponent = components.find((component) => component.name === FRONTEND_COMPONENT_NAME);

  return (
    <div className="space-y-6">
      <section className="card">
        <h2 className="mb-4 text-lg font-semibold">Overall Status</h2>
        <div className="flex flex-wrap items-center gap-3">
          <span
            className={`inline-flex items-center rounded-full px-4 py-2 text-base font-semibold ${
              normalizeStatus(overallStatus) === 'healthy'
                ? 'bg-green-500/20 text-green-700 dark:text-green-400 ring-1 ring-inset ring-green-600/20 dark:ring-green-400/30'
                : normalizeStatus(overallStatus) === 'degraded'
                  ? 'bg-amber-500/20 text-amber-700 dark:text-amber-400 ring-1 ring-inset ring-amber-600/20 dark:ring-amber-400/30'
                  : normalizeStatus(overallStatus) === 'unhealthy'
                    ? 'bg-red-500/20 text-red-700 dark:text-red-400 ring-1 ring-inset ring-red-600/20 dark:ring-red-400/30'
                    : 'bg-gray-500/20 text-gray-700 dark:text-gray-400 ring-1 ring-inset ring-gray-600/20 dark:ring-gray-400/30'
            }`}
          >
            {getStatusLabel(overallStatus)}
          </span>
          <span className="text-sm text-gray-600 dark:text-gray-300">
            Current platform health based on service checks and component readiness.
          </span>
        </div>
      </section>

      <section className="card">
        <h2 className="mb-4 text-lg font-semibold">Server Information</h2>
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <DetailRow label="Environment" value={systemInfo.environment || '—'} />
          <DetailRow label="Runtime" value={systemInfo.runtime || '—'} />
          <DetailRow label="API Version" value={backendComponent?.version || systemInfo.version || '—'} />
          <DetailRow label="API Commit" value={truncateCommitSha(backendComponent?.commitSha)} />
          <DetailRow
            label="API Build Timestamp"
            value={backendComponent?.buildTimestamp ? formatTimestamp(backendComponent.buildTimestamp) : '—'}
          />
          <DetailRow
            label="API Deployed At"
            value={backendComponent?.deployedAt ? formatTimestamp(backendComponent.deployedAt) : (systemInfo.deployedAt ? formatTimestamp(systemInfo.deployedAt) : '—')}
          />
          <DetailRow label="Frontend Version" value={frontendComponent?.version || '—'} />
          <DetailRow label="Frontend Commit" value={truncateCommitSha(frontendComponent?.commitSha)} />
          <DetailRow
            label="Frontend Build Timestamp"
            value={frontendComponent?.buildTimestamp ? formatTimestamp(frontendComponent.buildTimestamp) : '—'}
          />
          <DetailRow
            label="Frontend Deployed At"
            value={frontendComponent?.deployedAt ? formatTimestamp(frontendComponent.deployedAt) : '—'}
          />
          <DetailRow label="Uptime" value={systemInfo.uptime || '—'} />
          <DetailRow
            label="Server Timestamp"
            value={systemInfo.serverTimestamp ? formatTimestamp(systemInfo.serverTimestamp) : '—'}
          />
          <DetailRow
            label="Timestamp (Relative)"
            value={systemInfo.serverTimestamp ? formatRelativeTime(systemInfo.serverTimestamp) : '—'}
          />
        </dl>
      </section>

      <section className="card">
        <h2 className="mb-4 text-lg font-semibold">Components</h2>
        {components.length > 0 ? (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {components.map((component) => (
              <article
                key={component.name}
                className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900"
              >
                <div className="mb-3 flex items-start justify-between gap-2">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">{component.name}</h3>
                  <Badge size="sm" variant={getStatusVariant(component.status)}>
                    {getStatusLabel(component.status)}
                  </Badge>
                </div>

                {component.statusMessage && normalizeStatus(component.status) !== 'healthy' && normalizeStatus(component.status) !== 'self_reported' ? (
                  <p className="mb-3 text-sm text-gray-700 dark:text-gray-300">{component.statusMessage}</p>
                ) : null}

                <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Version
                    </dt>
                    <dd className="text-gray-900 dark:text-gray-100">{component.version || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Commit SHA
                    </dt>
                    <dd className="font-mono text-gray-900 dark:text-gray-100">{truncateCommitSha(component.commitSha)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Build Timestamp
                    </dt>
                    <dd className="text-gray-900 dark:text-gray-100">
                      {component.buildTimestamp ? formatTimestamp(component.buildTimestamp) : '—'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Deployed At
                    </dt>
                    <dd className="text-gray-900 dark:text-gray-100">
                      {component.deployedAt ? formatTimestamp(component.deployedAt) : '—'}
                    </dd>
                  </div>
                </dl>

                {component.details ? (
                  <p className="mt-3 rounded border border-dashed border-gray-300 px-3 py-2 text-sm text-gray-700 dark:border-gray-600 dark:text-gray-300">
                    {component.details}
                  </p>
                ) : null}
              </article>
            ))}
          </div>
        ) : (
          <EmptyState title="No components available" message="No component health details were returned." />
        )}
      </section>

      {systemInfo.serviceEndpoints && systemInfo.serviceEndpoints.length > 0 ? (
        <section className="card">
          <h2 className="mb-4 text-lg font-semibold">Service Endpoints</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="px-3 py-3 font-semibold">Name</th>
                  <th className="px-3 py-3 font-semibold">URL</th>
                  <th className="px-3 py-3 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white dark:divide-gray-800 dark:bg-gray-900">
                {systemInfo.serviceEndpoints.map((endpoint) => (
                  <tr key={`${endpoint.name}-${endpoint.url}`} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-3 py-3 font-medium text-gray-900 dark:text-gray-100">{endpoint.name}</td>
                    <td className="px-3 py-3">
                      <a
                        href={endpoint.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-700 underline underline-offset-2 hover:text-primary-600 dark:text-primary-400 dark:hover:text-primary-300"
                      >
                        {endpoint.url}
                      </a>
                    </td>
                    <td className="px-3 py-3">
                      <Badge size="sm" variant={getStatusVariant(endpoint.status)}>
                        {getStatusLabel(endpoint.status)}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </div>
  );
}

export function SystemInfoPage() {
  const [activeTab, setActiveTab] = useState<ActiveTab>('health-status');
  const systemInfoQuery = useQuery<SystemInfoResponse, Error>({
    queryKey: ['systemInfo'],
    queryFn: async () => {
      const data = await apiClient.getSystemInfo();
      return applyFrontendBuildOverride(data);
    },
    refetchInterval: 60_000,
  });

  const lastRefreshedLabel = systemInfoQuery.dataUpdatedAt
    ? formatTimestamp(new Date(systemInfoQuery.dataUpdatedAt))
    : '—';
  const lastRefreshedRelative = systemInfoQuery.dataUpdatedAt
    ? formatRelativeTime(new Date(systemInfoQuery.dataUpdatedAt))
    : '—';

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">System Information</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            Health, deployment metadata, and release history for the admin platform.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="text-right text-xs text-gray-600 dark:text-gray-400">
            <p>Last refreshed: {lastRefreshedLabel}</p>
            <p>{lastRefreshedRelative}</p>
          </div>
          <button
            type="button"
            onClick={() => void systemInfoQuery.refetch()}
            disabled={systemInfoQuery.isFetching}
            className="rounded border border-gray-300 bg-white px-3 py-2 text-sm font-medium hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700"
            aria-label="Refresh system information"
          >
            {systemInfoQuery.isFetching ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="mb-6 border-b border-gray-200 dark:border-gray-700" role="tablist" aria-label="System info tabs">
        <button
          id="tab-health-status"
          role="tab"
          type="button"
          aria-selected={activeTab === 'health-status'}
          aria-controls="panel-health-status"
          onClick={() => setActiveTab('health-status')}
          className={`mr-4 border-b-2 px-1 py-3 text-sm font-medium ${
            activeTab === 'health-status'
              ? 'border-primary-700 text-primary-700 dark:border-primary-400 dark:text-primary-400'
              : 'border-transparent text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100'
          }`}
        >
          Health Status
        </button>
        <button
          id="tab-version-history"
          role="tab"
          type="button"
          aria-selected={activeTab === 'version-history'}
          aria-controls="panel-version-history"
          onClick={() => setActiveTab('version-history')}
          className={`border-b-2 px-1 py-3 text-sm font-medium ${
            activeTab === 'version-history'
              ? 'border-primary-700 text-primary-700 dark:border-primary-400 dark:text-primary-400'
              : 'border-transparent text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100'
          }`}
        >
          Version History
        </button>
      </div>

      {activeTab === 'health-status' ? (
        <section
          id="panel-health-status"
          role="tabpanel"
          aria-labelledby="tab-health-status"
          className="space-y-4"
        >
          {systemInfoQuery.isLoading ? <SkeletonDetail /> : null}

          {!systemInfoQuery.isLoading && systemInfoQuery.isError ? (
            <div className="card">
              <h2 className="text-lg font-semibold text-red-700 dark:text-red-400">Unable to load system info</h2>
              <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                {systemInfoQuery.error?.message || 'An unexpected error occurred while loading system information.'}
              </p>
              <button
                type="button"
                onClick={() => void systemInfoQuery.refetch()}
                className="mt-4 rounded border border-gray-300 px-3 py-2 text-sm font-medium hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700"
              >
                Try again
              </button>
            </div>
          ) : null}

          {!systemInfoQuery.isLoading && !systemInfoQuery.isError && !systemInfoQuery.data ? (
            <EmptyState
              title="System info unavailable"
              message="No system information was returned from the API."
              action={(
                <button
                  type="button"
                  onClick={() => void systemInfoQuery.refetch()}
                  className="rounded border border-gray-300 px-3 py-2 text-sm font-medium hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700"
                >
                  Refresh
                </button>
              )}
            />
          ) : null}

          {!systemInfoQuery.isLoading && !systemInfoQuery.isError && systemInfoQuery.data ? (
            <HealthStatusTab systemInfo={systemInfoQuery.data} />
          ) : null}
        </section>
      ) : (
        <section
          id="panel-version-history"
          role="tabpanel"
          aria-labelledby="tab-version-history"
        >
          <VersionHistoryTab />
        </section>
      )}
    </div>
  );
}
